/**
 * Turning stored values into readable ones.
 *
 * The rule that matters most here: **missing data never renders as a number**
 * (REQ-WEB-007). A blank cell shown as 0 is not a display bug, it is a false
 * claim — it says a lens weighs nothing, and it sorts to the top of "lightest
 * first". Every path below that could produce a number from an absent value
 * returns the unknown marker instead.
 */
import type { Attribute, Cell } from "./api";
import { toDisplay } from "./api";

export const UNKNOWN = "—";
/**
 * A spec that cannot apply to this lens, as a rule or a source says (REQ-WEB-007,
 * D45). Unlike a dash it is a finding, not a gap.
 */
export const NOT_APPLICABLE = "n/a";

/** Trim float noise without inventing precision the value never had. */
export function formatNumber(n: number, step: number | null): string {
  if (!Number.isFinite(n)) return UNKNOWN;
  // Decimals implied by the registry's step: 0.01 → 2, 0.1 → 1, 1 → 0.
  const dp = step && step < 1 ? Math.min(4, Math.ceil(-Math.log10(step))) : 0;
  const fixed = n.toFixed(dp);
  // 2.50 → 2.5, 400.0 → 400; never 4e+2.
  return dp > 0 ? fixed.replace(/\.?0+$/, "") : fixed;
}

/** From here up a computed value is shown in scientific form. */
const HUGE = 1e12;

/**
 * A computed value, which has no registry entry to say how precise it is.
 *
 * The server rounds to six decimal places (spec 060 §3.2), which is the right
 * scale to *store* a ratio and the wrong one to read: "5.796497 g/mm" claims a
 * precision that came from the division rather than from any measurement.
 * Digits are chosen by magnitude so a density and a volume are both legible.
 */
export function formatComputed(n: number): string {
  if (!Number.isFinite(n)) return UNKNOWN;
  const abs = Math.abs(n);
  // An enormous result is shown to a few significant digits, not seventeen:
  // "2.707e+195", not "2.7071782767869983e+195" (REQ-FX-009). Past this size
  // fixed decimals would be a wall of digits on a phone card.
  if (abs >= HUGE) return n.toExponential(3).replace(/\.?0+e/, "e");
  const dp = abs >= 100 ? 0 : abs >= 10 ? 1 : abs >= 1 ? 2 : abs >= 0.01 ? 3 : 6;
  const fixed = n.toFixed(dp);
  return dp > 0 ? fixed.replace(/\.?0+$/, "") : fixed;
}

/**
 * True when a cell has nothing to cite: a worked-out value, or a spec a rule
 * says cannot apply. A "not applicable" a source stated for this lens does have
 * a citation, so it opens like any value (REQ-WEB-005, REQ-DM-008 as amended
 * by D45).
 */
export function hasNoCitation(cell: Cell | undefined): boolean {
  return !!cell?.computed || (cell?.state === "not_applicable" && cell.source_id == null);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * A release date at the precision its source printed (REQ-DM-028, D48).
 *
 * The API's value is already that string — "2023", "2023-09" or "2023-09-14" —
 * and this reads it as words without ever completing it: a year stays a year,
 * a month stays a month. "2023" must never come out as "1 Jan 2023". Parsed from
 * the text, not through `Date`, so no time zone can move a day.
 */
export function formatPartialDate(v: string): string {
  const m = /^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/.exec(v);
  if (!m) return v;
  const [, year, month, day] = m;
  if (!month) return year!;
  const name = MONTHS[Number(month) - 1];
  if (!name) return v;
  return day ? `${Number(day)} ${name} ${year}` : `${name} ${year}`;
}

/**
 * An amount of money in the currency it was published in: $1,299, ¥198,000
 * (REQ-DM-031). The amount is never converted; the currency is only how it is
 * written. A code the browser does not know is printed after the number.
 */
export function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency", currency, currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 0, maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString("en-US")} ${currency}`;
  }
}

export function formatCell(attr: Attribute | undefined, cell: Cell | undefined): string {
  if (cell?.state === "not_applicable") return NOT_APPLICABLE;
  if (!cell || cell.value === null || cell.value === undefined) return UNKNOWN;
  const v = cell.value;

  if (attr?.unit_family === "currency" && typeof v === "number" && cell.unit) {
    return formatMoney(v, cell.unit);
  }

  if (attr?.datatype === "date" && typeof v === "string") return formatPartialDate(v);
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "string") {
    if (!attr) return v;
    const opt = attr.options.find((o) => o.value === v);
    return opt ? opt.label : v;
  }
  if (typeof v === "number") {
    // No attribute means a computed column: there is no registry entry, no
    // unit and no display step, so it is formatted by magnitude instead.
    if (!attr) return formatComputed(v);
    return formatNumber(toDisplay(attr, v), attr.display_step);
  }
  return UNKNOWN;
}

/**
 * A number wearing its unit the way people write it.
 *
 * Some units are prefixes, not suffixes: an aperture is "f/2.8" and never
 * "2.8 f". Everywhere this rule is *not* applied, the same quantity gets
 * spelled two different ways on one screen — the results table said "f/128"
 * while the aperture slider said "128 f", which reads as a different kind of
 * number rather than the same one.
 */
export function withUnit(attr: Attribute | undefined, text: string): string {
  if (!attr) return text;
  if (attr.unit_family === "none") return text;
  // A price already carries its currency in the text (REQ-DM-031).
  if (attr.unit_family === "currency") return text;
  return withUnitCode(attr.display_unit, text);
}

/**
 * The same rule for a unit given as a bare code, such as the one a citation
 * records. The pop-up under a value used to print the code itself ("7 n",
 * "16 f", "0.1 x") beside a row that said "7", "f/16" and "0.1×" (#213).
 */
export function withUnitCode(u: string | null | undefined, text: string): string {
  // "n" is the registry's unit for a count (elements, groups, blades): a
  // number of things, said without a unit. "28 n" read as a typo (#210).
  if (!u || u === "n") return text;
  if (u === "f") return `f/${text}`;
  if (u === "x") return `${text}×`;
  return `${text} ${u}`;
}

/**
 * One cited value as the pop-up under a cell shows it (#213): the figure as the
 * source printed it, in the same words as the row above it. A choice shows its
 * option's label rather than its stored code, a yes/no shows Yes or No, and a
 * unit follows the shared rule above.
 */
export function citedValueText(
  attr: Attribute | undefined,
  value: number | string | boolean | null | undefined,
  unit: string | null | undefined,
): string {
  if (value === null || value === undefined) return UNKNOWN;
  if (attr?.datatype === "date" && typeof value === "string") return formatPartialDate(value);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string") {
    return attr?.options.find((o) => o.value === value)?.label ?? value;
  }
  // The attribute's own "none" family means the unit code is bookkeeping.
  if (attr?.unit_family === "none") return String(value);
  if (attr?.unit_family === "currency" && unit) return formatMoney(value, unit);
  return withUnitCode(unit, String(value));
}

/** The value plus its unit, as a person would say it. */
export function formatCellWithUnit(attr: Attribute | undefined, cell: Cell | undefined): string {
  const text = formatCell(attr, cell);
  if (text === UNKNOWN || !attr) return text;
  if (typeof cell?.value !== "number") return text;
  return withUnit(attr, text);
}

/** Focal length reads as one fact — "24–70 mm" — not as two columns. */
export function focalSummary(
  row: { values: Record<string, Cell> },
  byKey: Map<string, Attribute>,
): string | null {
  const lo = row.values["focal_length_min_mm"];
  const hi = row.values["focal_length_max_mm"];
  const a = byKey.get("focal_length_min_mm");
  if (!a || !lo || typeof lo.value !== "number") return null;
  const loText = formatNumber(toDisplay(a, lo.value), a.display_step);
  if (!hi || typeof hi.value !== "number" || hi.value === lo.value) return `${loText} mm`;
  const hiText = formatNumber(toDisplay(a, hi.value), a.display_step);
  return `${loText}–${hiText} mm`;
}

const COUNT_FMT = new Intl.NumberFormat();
export const formatCount = (n: number) => COUNT_FMT.format(n);


/**
 * What to call a lens: its maker and model as one prose name (REQ-WEB-031).
 *
 * All nine lens sites surveyed on 2026-09-10 write it this way, because the
 * maker is part of the name rather than a separate field. The guard is the
 * whole reason this is a function: 122 of 2,073 model names already begin with
 * their maker, so a plain join produces "7Artisans 7Artisans 10mm f/2.8".
 */
export function lensName(maker: string | undefined, model: string): string {
  if (!maker) return model;
  const m = maker.trim();
  if (!m) return model;
  return model.toLowerCase().startsWith(m.toLowerCase()) ? model : `${m} ${model}`;
}

/** The site's canonical domain, as its own citations store it (REQ-DM-026). */
const OWN_HOSTS = new Set(["lens-specs.com", "www.lens-specs.com"]);

/**
 * Where a citation link should go from the page showing it.
 *
 * A value lens-specs derives cites the site's own methodology page, stored as
 * the canonical https://lens-specs.com/... address so the row is still a
 * complete citation in the export and in anyone's copy. Shown on a local or
 * tunnelled instance, that address would send the reader to production — or,
 * before launch, to nothing. So a link on the site's own domain is re-based on
 * the origin actually serving the page. Every other host is left exactly as
 * stored: rewriting a third party's citation would be changing the evidence.
 */
export function citationHref(url: string, origin: string = location.origin): string {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return url;
  }
  if (!OWN_HOSTS.has(u.hostname.toLowerCase())) return url;
  return origin + u.pathname + u.search + u.hash;
}
